'use client'

import { useState } from 'react'
import { Plus } from 'lucide-react'
import FieldFade from '@/components/field/FieldFade'
import FieldSection from '@/components/field/FieldSection'
import JsonLd from '@/components/seo/JsonLd'
import { faqPage } from '@/lib/seo/jsonld'
import { HOME_FAQ } from '@/lib/copy/homepage'
import { track } from '@/lib/analytics'

export default function Faq() {
  const [open, setOpen] = useState<number | null>(null)

  return (
    <FieldSection
      variant="faq"
      id="faq"
      className="px-4 py-20 sm:px-6 md:py-28"
    >
      {/* Same array the page renders below, so the markup and the visible
          text can never drift (components/seo/FaqSection.tsx's own rule). */}
      <JsonLd data={faqPage(HOME_FAQ)} />
      <div className="mx-auto grid max-w-[1120px] gap-10 sm:gap-12 lg:grid-cols-[0.7fr_1.3fr]">
        <div className="relative self-start">
          <FieldFade />
          <h2 data-field-text className="relative text-display-3 font-bold text-[var(--rt-ink)]">
            Questions
          </h2>
        </div>

        {/* One pool for the list, not one per row: nine of them stacked would
            be nine soft bands, which is a pattern rather than a fade. */}
        <div className="relative">
          <FieldFade />
          <ul className="relative border-t border-[var(--app-border)]">
          {HOME_FAQ.map((item, i) => {
            const isOpen = open === i
            return (
              <li key={item.question} className="border-b border-[var(--app-border)]">
                <button
                  onClick={() => {
                    setOpen(isOpen ? null : i)
                    // Track opens only — closes are noise for the "which
                    // questions matter" read.
                    if (!isOpen) track('landing_faq_toggled', { index: i, question: item.question })
                  }}
                  className="flex w-full items-center gap-5 py-5 text-left"
                  aria-expanded={isOpen}
                >
                  <span data-field-text className="font-mono text-caption text-[var(--rt-ink-faint)]">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span data-field-text className="flex-1 text-title font-medium text-[var(--rt-ink)]">
                    {item.question}
                  </span>
                  <Plus
                    className="h-4 w-4 shrink-0 text-[var(--rt-accent)] transition-transform duration-[var(--rt-spring-ms)] ease-[var(--rt-ease-spring)]"
                    style={{ transform: isOpen ? 'rotate(45deg)' : 'none' }}
                  />
                </button>
                <div
                  className="grid transition-[grid-template-rows] duration-[var(--rt-spring-ms)] ease-[var(--rt-ease-spring)]"
                  style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
                >
                  <div className="overflow-hidden">
                    <p data-field-text className="max-w-xl pb-6 pl-9 pr-0 text-body text-[var(--rt-ink-dim)] sm:pl-[2.6rem]">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </li>
            )
          })}
          </ul>
        </div>
      </div>
    </FieldSection>
  )
}
